/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { Card, Divider, InlineNotification, Typography2 } from '@coursera/cds-core';

import { delink, dismissPasswordDialog, getLinkedAccounts } from 'bundles/third-party-auth/actions/AccountLinkActions';
import AccountLinkApple from 'bundles/third-party-auth/components/AccountLink/AccountLinkApple';
import AccountLinkFacebook from 'bundles/third-party-auth/components/AccountLink/AccountLinkFacebook';
import AccountLinkGoogle from 'bundles/third-party-auth/components/AccountLink/AccountLinkGoogle';
import AccountLinkSSO from 'bundles/third-party-auth/components/AccountLink/AccountLinkSSO';
import AccountLinkSlack from 'bundles/third-party-auth/components/AccountLink/AccountLinkSlack';
import AccountLinkZoom from 'bundles/third-party-auth/components/AccountLink/AccountLinkZoom';
import PasswordDialog from 'bundles/third-party-auth/components/AccountLink/PasswordDialog';
import { isSsoLinked, isSsoType } from 'bundles/third-party-auth/components/AccountLink/utils/linkedAccountStatus';
import { getOAuthDisabledMessage } from 'bundles/third-party-auth/components/AccountLink/utils/messages';
import type { SSO_TYPE } from 'bundles/third-party-auth/lib';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';

import _t from 'i18n!nls/third-party-auth';

const styles = {
  card: css`
    &.cds-Card-base .cds-Card-innerCard {
      padding: var(--cds-spacing-200);
    }
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    margin: 0;
  `,
  description: css`
    margin: 0;
  `,
  rows: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-100);
  `,
};

const LinkedAccountsCard = () => {
  const { linked } = useFluxibleStore(AccountLinkStore, (store) => ({
    linked: store.linked || {},
  }));

  const ssoLinked = isSsoLinked(linked);
  const oauthDisabledMessage = getOAuthDisabledMessage(linked);

  return (
    <Card css={styles.card} interactive={false}>
      <section css={styles.section}>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {_t('Linked accounts')}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
          {_t('Enable one-click login and receive more personalized course recommendations.')}
        </Typography2>

        {ssoLinked && (
          <InlineNotification severity="information">
            {_t('Unlink your organization account to connect other accounts.')}
          </InlineNotification>
        )}
        {oauthDisabledMessage && <InlineNotification severity="warning">{oauthDisabledMessage}</InlineNotification>}

        <div css={styles.rows}>
          <AccountLinkFacebook />
          <Divider />
          <AccountLinkApple />
          <Divider />
          <AccountLinkGoogle />
        </div>
      </section>
    </Card>
  );
};

const AccountLink = () => {
  const [passwordDialogProvider, setPasswordDialogProvider] = useState<SSO_TYPE | null>(null);
  const ssoButtonRef = useRef<HTMLButtonElement>(null);
  const executeAction = useFluxibleAction();
  const { requirePassword, currentAccountType } = useFluxibleStore(AccountLinkStore, (store) => ({
    requirePassword: store.requirePassword,
    currentAccountType: store.currentAccountType || '',
  }));
  const prevRequirePassword = useRef(false);

  useEffect(() => {
    executeAction(getLinkedAccounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  }, []);

  useEffect(() => {
    if (requirePassword && !prevRequirePassword.current && currentAccountType && isSsoType(currentAccountType)) {
      setPasswordDialogProvider(currentAccountType);
    }
    prevRequirePassword.current = requirePassword;
  }, [requirePassword, currentAccountType]);

  const handleClosePasswordDialog = () => {
    setPasswordDialogProvider(null);
    executeAction(dismissPasswordDialog);
  };

  const handleDelink = useCallback<(password: string) => void>(
    (password: string) => {
      if (passwordDialogProvider) {
        executeAction(delink, {
          accountType: passwordDialogProvider,
          currentAccountType: passwordDialogProvider,
          password,
        });
      }
    },
    [executeAction, passwordDialogProvider]
  );

  return (
    <React.Fragment>
      <LinkedAccountsCard />
      <PasswordDialog
        open={passwordDialogProvider !== null}
        onDelink={handleDelink}
        onClose={handleClosePasswordDialog}
        returnFocusRef={passwordDialogProvider && isSsoType(passwordDialogProvider) ? ssoButtonRef : undefined}
      />
      <AccountLinkSSO buttonRef={ssoButtonRef} />
      <AccountLinkZoom />
      <AccountLinkSlack />
    </React.Fragment>
  );
};

export default AccountLink;
